<script>

	import { onMount, onDestroy, createEventDispatcher } from 'svelte';
	import { crossfade, fly, fade } from 'svelte/transition';
	import { flip } from 'svelte/animate';
	import { quintOut } from 'svelte/easing';

	import Poll from "./Polls/Poll.svelte";
	import NewPoll from "./Polls/NewPoll.svelte";

	import Button from "../../../ui/Button.svelte";
	import Slate from "../../../ui/Slate.svelte";

	import {	bigError, attendee, syncClient, session, showSettings, settings } from '../../../lib/stores.js';
	import { randomIntFromInterval } from '../../../lib/utils.js';
	import {	autoblur } from '../../../lib/autoblur.js';

	const dispatch = createEventDispatcher();

	const [send, receive] = crossfade({
		duration: 0,
		fallback(node, params) {
			return {
				duration: 300,
				easing: quintOut,
				css: t => `
					transform: scale(${1 - ((1 - t) / 32)});
					opacity: ${t}
				`
			};
		}
	});

	let mounted = false;
	let createOverlay = false;

	let channel;
	let polls = [];

	let mod = false;

	$: mod = ($settings && $settings.room && (($settings.mode == 'presenter') || ($settings.mode == 'av'))) ? false : true;

	onMount(async () => {

		try {

			// console.log("opening session channel (polls)");
			channel = await $syncClient.list($session.syncChannel);

			channel.on('itemAdded', function(i) {
				if (i.item.data.type == 'poll') {
					addPoll(i.item);
				}
			});

			channel.on('itemUpdated', function(i) {
				if (i.item.data.type == 'poll') {
					updatePoll(i.item);
				}
			});

			channel.on('itemRemoved', function(i) {
				if (i.previousItemData.type == 'poll') {
					deletePoll(i.index);
				}
			});

			await getPolls();
			
		} catch (e) {

			console.log("Sync error", e, $session.syncChannel);
			
		}

		mounted = true;

	});

	onDestroy(() => {
		// console.log("closing session channel (polls)");
		if (channel) channel.close();
	});

	async function pollPageHandler(paginator) {
		paginator.items.forEach(function(item) {
			if (item.data.type == 'poll') {
				addPoll(item);
			}
		});
		return paginator.hasNextPage ? paginator.nextPage().then(pollPageHandler) : null;
	};

	async function getPolls() {
		// channel.getItems({
		// 	from: 0,
		// 	order: 'asc',
		// 	pageSize: 100
		// }).then(
		// 	pollPageHandler
		// ).catch(function(error) {
		// 	if (error.message.match('Maximum attempt')) {
		// 		// back off and try again later...
		// 		const smear = 1000 * randomIntFromInterval(5,10);
		// 		setTimeout(() => {
		// 			getPolls();
		// 		}, smear);
		// 	} else {
		// 		console.error('getPolls failed', error);
		// 	}
		// });
		let paginator;
		try {
			paginator = await channel.getItems({
				from: 0,
				order: 'asc',
				pageSize: 100
			});
		} catch (error) {
			if (error.message.match('Maximum attempt')) {
				// back off and try again later...
				const smear = 1000 * randomIntFromInterval(5,10);
				setTimeout(async () => {
					await getPolls();
				}, smear);
			} else {
				console.error('getPolls failed', error);
			}
		}
		if (paginator) await pollPageHandler(paginator);
	}

	function addPoll(item) {
		// console.log('addPoll', item.data);
		let poll = item.data.poll;
		poll.index = item.index;
		polls.unshift(poll);
		polls = polls;
	}

	async function updatePoll(item) {
		// console.log('updatePoll', item.data);
		const idx = item.index;
		for (let [i,r] of polls.entries()) {
			if (r.index == idx) {
				let poll = item.data.poll;
				poll.index = r.index;
				polls[i] = poll;
				break;
			}
		}
		polls = polls;
	}

	function deletePoll(idx) {
		// console.log('deletePoll', idx);
		polls = polls.filter(m => {
			if (m.index != idx) return m;
		});
	}

</script>

<style>
	.wrap {
		position: absolute;
		inset:  0;
		display: flex;
		flex-direction: column;
	}
	.wrap .polls {
		flex: 1 1 auto;
		overflow-y: auto;
		padding: 0 1rem 3rem 1rem;
		position: relative;
	}

	@media (max-width: 955px) {
		.wrap .polls {
			padding: 0 1rem 7rem 1rem;
		}
	}

	/*.wrap .actions {
		flex: 0 0 auto;
		border-top: 2px solid var(--blend-20);
		padding: 1rem 0;
		margin: 0 1rem;
	}*/
	.polls > div + div {
		margin-top: 3rem;
		border-top: 1px solid var(--blend-40);
	}

	/*.polls .slate {
		padding-top: 2rem;
	}*/

	.new {
		position: fixed;
		bottom: calc(2rem + env(safe-area-inset-bottom));
		right: 2rem;
	}
	.new :global(button) {
		width: 3.6rem !important;
		height: 3.6rem !important;
		box-shadow: 0 4px 8px var(--darkShadow);
	}
	.new :global(button > svg) {
		width: 2.8rem !important;
		height: 2.8rem !important;
	}
</style>

{#if mounted}
	<div class="wrap" in:fly={{ duration: 300, y: 20 }}>
		<div class="polls">
			{#each polls as poll (poll.index)}
				<div
					in:receive|local="{{ key: poll.index }}"
					out:send|local="{{ key: poll.index }}"
					animate:flip="{{ duration: 400 }}"
				>
					<Poll {poll} {mod}/>
				</div>
			{:else}
				<Slate>
					<p class="icon"><svg viewBox="0 0 32 32"><path d="M24.85 30h-21.7c-1.74 0-3.15-1.46-3.15-3.25v-21.5c0-1.79 1.41-3.25 3.15-3.25h21.78c1.7 0 3.07 1.41 3.07 3.15v21.6c0 1.79-1.41 3.25-3.15 3.25zm-21.56-26c-.71 0-1.29.62-1.29 1.38v21.24c0 .76.58 1.38 1.15 1.38h21.56c.84 0 1.29-.62 1.29-1.38v-21.33c0-.71-.54-1.29-1.2-1.29h-21.51zM8.41 25.3v-17.09c0-.69-.56-1.25-1.25-1.25s-1.25.56-1.25 1.25v17.09h2.5zM15.5 25.3v-12.29c0-.69-.56-1.25-1.25-1.25s-1.25.56-1.25 1.25v12.29h2.5zM22.59 25.3v-5.38c0-.69-.56-1.25-1.25-1.25s-1.25.56-1.25 1.25v5.38h2.5z"/></svg></p>
					<p class="label"><strong>No polls yet.</strong></p>
					{#if $attendee.type == 'login'}
						<p class="description">Add a poll to this session and ask your attendees to respond.</p>
					{:else}
						<p class="description">If the session organiser adds a poll, it will appear here.</p>
					{/if}
				</Slate>
			{/each}
		</div>
		<!-- {#if ($attendee.type == 'login')}
			<div class="actions">
				<Button label="Create poll" type="button" ghost={false} wide={true} on:click={() => { createOverlay = true }} />
			</div>
		{/if} -->
	</div>

	{#if !createOverlay && !$showSettings}
		<div class="new" in:fade={{ duration: 400, delay: 400 }} out:fade|local={{ duration: 200 }}>
			<Button on:click={() => { createOverlay = true }}>
				<svg viewBox="0 0 80 80"><path d="m31.96 59.32-12.66 3.33c-.55.15-1.14-.01-1.54-.42-.4-.4-.56-.99-.42-1.54l3.34-12.65z"/><path d="m49.05 19.64c3.12-3.12 8.19-3.12 11.31 0s3.12 8.19 0 11.31z"/><path d="m57.48 33.84-11.31-11.32-22.63 22.63 4.41 4.4 19.81-19.81 2.45 2.45-19.81 19.81 4.46 4.46z"/></svg>
			</Button>
		</div>
	{/if}
{/if}

{#if createOverlay}
	<NewPoll bind:createOverlay/>
{/if}